// WaitlistPage — Iter-58.9 (2026-06-02). Cold-traffic landing page
// for X / social shares that arrive when a visitor isn't ready to
// create a full account yet. Captures email + city + utm + referrer
// in `db.waitlist` so we can fire a Chairman's Bazooka or email blast
// later.
//
// Designed as a SECOND entry point, not a replacement for the
// landing page. The hamburger menu now links here under "Join the
// waitlist", and the public-share-card OG redirector can route cold
// arrivals here when ?wl=1 is appended.
//
// Pure additive — does not touch any auth flow.
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Globe, Mail, MapPin, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import Footer from "../components/Footer";

const WaitlistPage = () => {
  const [params] = useSearchParams();
  // Pre-fill from URL query (e.g. when the user came from a "Refer a
  // friend" link or an X-marketing post).
  const initialReferrer = (params.get("ref") || params.get("referrer") || "").trim().slice(0, 60);
  const [email, setEmail] = React.useState("");
  const [homeCity, setHomeCity] = React.useState("");
  const [referrer, setReferrer] = React.useState(initialReferrer);
  const [busy, setBusy] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: email.trim(),
        home_city: homeCity.trim() || null,
        referrer: referrer.trim() || null,
        utm_source: params.get("utm_source") || null,
        utm_medium: params.get("utm_medium") || null,
        utm_campaign: params.get("utm_campaign") || null,
      };
      const res = await api.post("/waitlist/join", payload);
      setSubmitted(true);
      toast.success(res.data?.message || "You're on the list.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 422) {
        toast.error(err.response?.data?.detail || "Please enter a valid email.");
      } else if (status === 429) {
        toast.error("Too many submissions. Try again later.");
      } else {
        toast.error("Could not join the waitlist. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md" data-testid="waitlist-page">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <Globe className="h-8 w-8 text-emerald-400" />
            <span className="text-2xl font-bold tracking-wide">GlobalEyes.TV</span>
          </div>

          {!submitted ? (
            <>
              <h1 className="text-3xl font-bold mb-2 text-center">
                Get on the list.
              </h1>
              <p className="text-gray-400 text-center mb-8">
                A live, location-based discovery feed of what's happening
                around the world — recorded by real people, watched live by
                everyone. Drop your email and we'll send you an invite as
                soon as we open your city.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-gray-300 text-sm block mb-1 flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" /> Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="waitlist-email"
                    autoComplete="email"
                    required
                    className="w-full bg-gray-900 border border-gray-700 focus:border-emerald-500 outline-none text-white px-3 py-2.5 rounded transition-colors"
                  />
                </div>

                <div>
                  <label className="text-gray-300 text-sm block mb-1 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> Home city
                    <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={homeCity}
                    onChange={(e) => setHomeCity(e.target.value)}
                    placeholder="e.g. Austin, TX"
                    data-testid="waitlist-city"
                    autoComplete="address-level2"
                    className="w-full bg-gray-900 border border-gray-700 focus:border-emerald-500 outline-none text-white px-3 py-2.5 rounded transition-colors"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    We open cities in order of waitlist volume — the more
                    people from your area, the sooner you get in.
                  </p>
                </div>

                <div>
                  <label className="text-gray-300 text-sm block mb-1">
                    Referred by{" "}
                    <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={referrer}
                    onChange={(e) => setReferrer(e.target.value)}
                    placeholder="@handle"
                    data-testid="waitlist-referrer"
                    autoComplete="off"
                    className="w-full bg-gray-900 border border-gray-700 focus:border-emerald-500 outline-none text-white px-3 py-2.5 rounded transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy || !email.trim()}
                  data-testid="waitlist-submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded transition-colors inline-flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ? "Joining\u2026" : "Join the waitlist"}
                </button>

                <p className="text-gray-500 text-xs text-center pt-2">
                  Already signed up?{" "}
                  <Link to="/login" className="text-emerald-400 hover:underline">
                    Sign in
                  </Link>
                  {" · "}
                  <Link to="/" className="text-emerald-400 hover:underline">
                    Back to home
                  </Link>
                </p>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="waitlist-success">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-900/40 border border-emerald-500/40 mb-4">
                <Check className="h-8 w-8 text-emerald-400" />
              </div>
              <h1 className="text-3xl font-bold mb-2">You're on the list.</h1>
              <p className="text-gray-400 mb-6">
                We'll email you the moment your city opens up. Until then,
                you can keep an eye on the live feed.
              </p>
              <Link
                to="/"
                className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-2.5 rounded transition-colors"
                data-testid="waitlist-go-home"
              >
                Watch the live feed
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WaitlistPage;
